import React, { useState, useEffect, ChangeEvent } from 'react';
import { StyleSheet } from 'react-native';
import { Order, ProstayGuestDto } from '../interfaces';

interface SelectReservationPopupProps {
    onClose: () => void;
    onConfirmSelection: (selectedRes: ProstayGuestDto | null) => void;
    order: Order | undefined;
}

const SelectReservationPopup: React.FC<SelectReservationPopupProps> = ({ onClose, onConfirmSelection, order }) => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<ProstayGuestDto[]>([]);
    const [searchingGuests, setSearchingGuests] = useState(false);
    const [selectedSearchResult, setSelectedSearchResult] = useState<ProstayGuestDto | null>(null);
    const [firstLoad, setFirstLoad] = useState(true);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
        fetchGuestSearchResults(value);
    };

    const fetchGuestSearchResults = async (value: string) => {
        if (!value) {
            setSearchResults([]);
            return;
        }

        const requestPayload = {
            currentPage: 1,
            searchTerm: value,
            pageSize: 1000,
            filterDuplicatesByEmail: false
        };

        setSearchingGuests(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload),
                credentials: 'include',
            });

            const data = await response.json();
            const guests: ProstayGuestDto[] = data.data || [];

            setSearchResults(guests);

            if (order?.prostayReservationNumber) {
                const match = guests.find(g => g.reservationNumber === order.prostayReservationNumber);
                if (match) {
                    setSelectedSearchResult(match);
                }
            }
        } catch (error) {
            console.error('Error fetching guest reservations:', error);
            setSearchResults([]);
        } finally {
            setSearchingGuests(false);
        }
    };

    useEffect(() => {
        const fetchInitialResults = async () => {
            const requestPayload = {
                currentPage: 1,
                searchTerm: '',
                pageSize: 1000,
                filterDuplicatesByEmail: false
            };

            setSearchingGuests(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestPayload),
                    credentials: 'include',
                });

                const data = await response.json();
                const guests: ProstayGuestDto[] = data.data || [];

                setSearchResults(guests);

                if (order?.prostayReservationNumber) {
                    const match = guests.find(g => g.reservationNumber === order.prostayReservationNumber);
                    if (match) {
                        setSelectedSearchResult(match);
                    }
                }
            } catch (error) {
                console.error('Error fetching guest reservations:', error);
                setSearchResults([]);
            } finally {
                setSearchingGuests(false);
                setFirstLoad(false);
            }
        };

        fetchInitialResults();
    }, [order]);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const styles = StyleSheet.create({
        popup: {
            width: windowSize.width > 900 ? windowSize.width * 0.40 : windowSize.width * 0.9,
            backgroundColor: '#fff',
            position: 'absolute',
            zIndex: 9998,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
    });

    return (
        <div style={styles.popup} className='ds-pop ds-pop--center'>
            <div className='d-flex px-3 pt-3 justify-content-end'>
                <button className='text-btn p-0 m-0 d-flex justify-content-center' onClick={onClose}>
                    <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 1L1 13M1 1L13 13" stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>

            <div className='px-4 pb-3'>
                <h5>Book On Room</h5>
                <p>Select the reservation to charge this order to their room.</p>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search guest name or email"
                    className="form-control mb-3"
                />

                {searchingGuests && <div>Loading...</div>}

                {!searchingGuests && searchResults.length > 0 && (
                    <div className='scrollable-container' style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {searchResults.map((guest) => {
                            const isSelected = selectedSearchResult?.reservationNumber === guest.reservationNumber;
                            return (
                                <div
                                    key={guest.reservationNumber}
                                    className={`border-box gap-2 p-2 mt-2 d-flex justify-content-between}`}>
                                    <div className='col'>
                                        <label style={{ fontWeight: '500', fontSize: 14, lineHeight: '14px' }} className='d-flex align-items-center'>{guest.firstName} {guest.lastName} {guest.roomNumber ? '(' + guest.roomNumber + ')' : ''} </label>
                                        <small style={{fontSize: 12}}>{guest.reservationNumber}</small>
                                    </div>
                                    <button className={isSelected ? 'gray-filled-btn' : 'blue-filled-btn'} onClick={() => setSelectedSearchResult(guest)}>
                                        {isSelected ? 'Selected' : 'Select'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className='p-2 d-flex justify-content-end' style={{ backgroundColor: '#F3F4F6' }}>
                <button className={selectedSearchResult ? 'white-filled-btn' : 'gray-filled-btn'} style={{cursor: selectedSearchResult ? "pointer" : "default"}}
                 onClick={() => selectedSearchResult && onConfirmSelection(selectedSearchResult)}>Confirm</button>
            </div>

            {firstLoad && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectReservationPopup;

